import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  CircularProgress
} from '@mui/material';
import { api } from '../../utils/api'; // Assuming api utility is set up

const AddItemDialog = ({ open, onClose, sessionId, onItemAdded }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const titleInputRef = useRef(null); // Ref for the title input

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    if (event.target.value) {
      setError(null); // Clear error when user starts typing in title
    }
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setError(null);
    // setLoading(false); // Loading is handled by the submit lifecycle
  };

  const handleSubmit = async (keepOpenAndReset = false) => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    setLoading(true);
    setError(null); // Clear previous errors on new submit attempt

    try {
      const newItemPayload = {
        sessionId,
        title: title.trim(),
        description: description.trim(),
        // externalId is not needed for manually added items
      };
      const response = await api.post('/items', newItemPayload);
      
      if (response.data) {
        onItemAdded(response.data); // Pass the newly created item back
        if (keepOpenAndReset) {
          resetForm(); 
          titleInputRef.current?.focus(); // Re-focus the title input
        } else {
          // For the original "Add Item" button, handleClose will be called by onClose() if it's setup that way
          // or we ensure onClose itself triggers the full dialog dismissal.
          // The handleClose function (wrapper around original onClose) ensures resetForm is called.
          onClose(); // This should trigger the dialog to close.
        }
      } else {
        throw new Error('No data returned from server on item creation.');
      }
    } catch (err) {
      console.error('Failed to create item:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to create item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm(); // Reset form state
    // setLoading(false); // Ensure loading is false if cancelled. Already in finally of handleSubmit.
    onClose(); // Call the original onClose passed from parent
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Estimation Item</DialogTitle>
      <DialogContent>
        {error && (
          <DialogContentText color="error" sx={{ mb: 2 }}>
            {error}
          </DialogContentText>
        )}
        <TextField
          inputRef={titleInputRef} // Assign ref
          autoFocus
          margin="dense"
          id="title"
          label="Item Title"
          type="text"
          fullWidth
          variant="outlined"
          value={title}
          onChange={handleTitleChange}
          required
          error={!!error && !title.trim()} // Show error on title if error is set and title is empty
          helperText={!!error && !title.trim() ? 'Title is required.' : ' '}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          id="description"
          label="Item Description (Optional)"
          type="text"
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={description}
          onChange={handleDescriptionChange}
        />
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={() => handleSubmit(true)} // Call handleSubmit with keepOpenAndReset = true
          variant="outlined" 
          color="primary" 
          disabled={loading || !title.trim()}
          sx={{ mr: 1 }} // Add some margin to separate from the next button
        >
          Add More
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button 
            onClick={() => handleSubmit(false)} // Call handleSubmit with keepOpenAndReset = false
            variant="contained" 
            color="primary" 
            disabled={loading || !title.trim()}
          >
            Add Item
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddItemDialog; 